import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import type { Announcement, PortalClass } from '../../types/portal';
import { Bell, AlertCircle, ChevronDown, RefreshCw } from 'lucide-react';

const ANNOUNCEMENTS_SEEN_EVENT = 'arabicroots:announcements-seen';

function formatDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Gestern';
  if (diff < 7) return `vor ${diff} Tagen`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
}

const StudentAnnouncementsPage: React.FC = () => {
  const { user } = useStudentAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [classFilter, setClassFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'important' | 'normal'>('all');
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get('a');
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [{ data: aData, error: aError }, { data: cData }] = await Promise.all([
        supabase
          .from('announcements')
          .select('*')
          .order('is_important', { ascending: false })
          .order('created_at', { ascending: false }),
        supabase.from('portal_classes').select('*').eq('is_active', true),
      ]);
      if (aError) throw aError;
      setAnnouncements((aData ?? []) as Announcement[]);
      setClasses((cData ?? []) as PortalClass[]);
    } catch (err) {
      if (import.meta.env.DEV) console.error('StudentAnnouncementsPage load error:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) return;
    const now = new Date().toISOString();
    try { localStorage.setItem(`announcements_seen_${user.id}`, now); } catch { /* ignore */ }
    void supabase
      .from('student_profiles')
      .update({ announcements_seen_at: now })
      .eq('id', user.id);
    window.dispatchEvent(new Event(ANNOUNCEMENTS_SEEN_EVENT));
  }, [user?.id]);

  // Direktlink aus dem Dashboard (?a=<id>): Filter zurücksetzen, damit die
  // Ankündigung sichtbar ist, dann hervorheben.
  useEffect(() => {
    if (!targetId || loading) return;
    if (!announcements.some(a => a.id === targetId)) return;
    setTypeFilter('all');
    setClassFilter('all');
    setHighlightId(targetId);
  }, [targetId, loading, announcements]);

  // Zur hervorgehobenen Ankündigung scrollen und Markierung nach kurzer Zeit entfernen
  useEffect(() => {
    if (!highlightId) return;
    const el = document.getElementById(`ann-${highlightId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightId(null), 2600);
    return () => clearTimeout(t);
  }, [highlightId]);

  const filtered = announcements.filter(a => {
    if (typeFilter === 'important' && !a.is_important) return false;
    if (typeFilter === 'normal' && a.is_important) return false;
    if (classFilter === 'all') return true;
    if (classFilter === 'global') return a.class_id === null;
    return a.class_id === classFilter;
  });

  const classMap = Object.fromEntries(classes.map(c => [c.id, c.title]));
  const importantCount = filtered.filter(a => a.is_important).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 dark:text-red-400 mb-1">
            Neuigkeiten
          </p>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Ankündigungen</h1>
        </div>
        {!loading && filtered.length > 0 && (
          <p className="text-sm text-gray-400 dark:text-neutral-500 pb-0.5">
            {filtered.length} {filtered.length === 1 ? 'Eintrag' : 'Einträge'}
            {importantCount > 0 && (
              <> · <span className="text-[#970000] dark:text-red-400 font-semibold">{importantCount} wichtig</span></>
            )}
          </p>
        )}
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Wichtig/Normal-Filter */}
        <div className="inline-flex items-center gap-1 bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-2xl p-1">
          {([
            { value: 'all', label: 'Alle' },
            { value: 'important', label: 'Wichtig' },
            { value: 'normal', label: 'Normal' },
          ] as const).map(opt => (
            <button
              key={opt.value}
              onClick={() => setTypeFilter(opt.value)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                typeFilter === opt.value
                  ? 'bg-[#970000] text-white shadow-sm'
                  : 'text-gray-500 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800'
              }`}
            >
              {opt.value === 'important' && <AlertCircle className="h-3.5 w-3.5" />}
              {opt.label}
            </button>
          ))}
        </div>

        {/* Klassen-Filter */}
        {classes.length > 0 && (
          <div className="relative w-full sm:w-64">
            <select
              value={classFilter}
              onChange={e => setClassFilter(e.target.value)}
              className="w-full appearance-none px-4 py-2.5 pr-10 border border-gray-200 dark:border-neutral-800 rounded-2xl outline-none bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-neutral-100 focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] transition-colors cursor-pointer"
            >
              <option value="all">Alle Ankündigungen</option>
              <option value="global">Allgemeine Ankündigungen</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-neutral-500 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 h-28" />
          ))}
        </div>
      ) : loadError ? (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-red-100 dark:border-red-900/50 shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-950/40 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-7 w-7 text-red-400" />
          </div>
          <p className="font-semibold text-gray-900 dark:text-neutral-100 mb-1">Ankündigungen konnten nicht geladen werden</p>
          <p className="text-sm text-gray-400 dark:text-neutral-500 mb-4">Bitte prüfe deine Verbindung und versuche es erneut.</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#970000] dark:text-red-400 hover:underline"
          >
            <RefreshCw className="h-4 w-4" />
            Erneut laden
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center mx-auto mb-4">
            <Bell className="h-7 w-7 text-gray-300 dark:text-neutral-600" />
          </div>
          <p className="font-semibold text-gray-900 dark:text-neutral-100 mb-1">Keine Ankündigungen</p>
          <p className="text-sm text-gray-400 dark:text-neutral-500">
            {classFilter === 'all' && typeFilter === 'all'
              ? 'Es gibt noch keine Ankündigungen.'
              : 'Für diese Auswahl gibt es keine Ankündigungen.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(a => (
            <div
              key={a.id}
              id={`ann-${a.id}`}
              className={`bg-white dark:bg-neutral-900 rounded-2xl border shadow-sm overflow-hidden transition-all duration-300 scroll-mt-24 hover:shadow-[0_6px_24px_rgba(151,0,0,0.08)] ${
                highlightId === a.id
                  ? 'border-[#970000] dark:border-red-500 ring-2 ring-[#970000]/40 dark:ring-red-500/50 shadow-[0_6px_24px_rgba(151,0,0,0.12)]'
                  : a.is_important
                    ? 'border-gray-200 dark:border-neutral-800 border-l-[3px] border-l-[#970000] dark:border-l-red-500'
                    : 'border-gray-200 dark:border-neutral-800 hover:border-[#970000]/40 dark:hover:border-red-500/50'
              }`}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {a.is_important ? (
                      <div className="shrink-0 w-8 h-8 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center mt-0.5">
                        <AlertCircle className="h-4 w-4 text-[#970000] dark:text-red-400" />
                      </div>
                    ) : (
                      <div className="shrink-0 w-8 h-8 rounded-xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center mt-0.5">
                        <Bell className="h-3.5 w-3.5 text-gray-400 dark:text-neutral-500" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <h3 className="font-bold text-gray-900 dark:text-neutral-100 leading-snug">{a.title}</h3>
                        {a.is_important && (
                          <span className="text-[10px] font-bold tracking-wider uppercase bg-[#970000]/10 dark:bg-red-500/15 text-[#970000] dark:text-red-400 px-2 py-0.5 rounded-full">
                            Wichtig
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-gray-400 dark:text-neutral-500">{formatDate(a.created_at)}</span>
                        <span className="text-gray-200 dark:text-neutral-700">·</span>
                        <span className="text-xs text-gray-400 dark:text-neutral-500">
                          {a.class_id ? (classMap[a.class_id] ?? 'Klasse') : 'Alle Schüler'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm font-bold text-gray-900 dark:text-neutral-100 leading-relaxed whitespace-pre-wrap pl-11">
                  {a.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentAnnouncementsPage;
